// SidebarDrawer.js

import React from "react";
import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { Box } from "@mui/material";

const openedMixin = (theme, drawerWidth) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
    duration: 300,
  }),
  overflowX: "hidden",
  backgroundColor: "#ffffff",
  borderRight: "1px solid rgba(226,232,240,0.8)",
  boxShadow: "2px 0 12px rgba(0,0,0,0.1)",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
    duration: 300,
  }),
  overflowX: "hidden",
  width: 64, // Show narrow sidebar with icons only
  backgroundColor: "#ffffff",
  borderRight: "1px solid rgba(226,232,240,0.8)",
  boxShadow: "2px 0 12px rgba(0,0,0,0.1)",
});

const DrawerStyled = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open, drawerwidth }) => ({
  width: drawerwidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  zIndex: theme.zIndex.drawer,
  ...(open && {
    ...openedMixin(theme, drawerwidth),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme, drawerwidth),
      zIndex: theme.zIndex.drawer,
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      zIndex: theme.zIndex.drawer,
    },
  }),
}));

const SidebarDrawer = ({
  open,
  setOpen,
  mobileOpen,
  setMobileOpen,
  isMobile,
  drawerWidth,
  children,
}) => {
  if (isMobile) {
    // Show a swipeable drawer on mobile
    return (
      <SwipeableDrawer
        variant="temporary"
        anchor="bottom"
        open={mobileOpen}
        onClose={() => {
          setMobileOpen(false);
          // Also update parent state when closing on mobile
          if (setOpen) {
            setOpen(false);
          }
        }}
        onOpen={() => {
          setMobileOpen(true);
          // Also update parent state when opening on mobile
          if (setOpen) {
            setOpen(true);
          }
        }}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: "100%",
            maxHeight: "85vh",
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
            borderTop: "1px solid rgba(226,232,240,0.6)",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 2,
            pt: 1,
            maxHeight: "85vh",
            overflowY: "auto",
          }}
        >
          {/* Drag handle indicator for mobile */}
          <Box
            sx={{
              width: "40px",
              height: "4px",
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "2px",
              margin: "0 auto 16px auto",
            }}
          />
          {children}
        </Box>
      </SwipeableDrawer>
    );
  }

  // Otherwise, show a permanent drawer on desktop that can be toggled
  // Always render the drawer, but with different width based on open state
  return (
    <DrawerStyled 
      variant="permanent" 
      open={open} 
      drawerwidth={drawerWidth}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Scrollable main content area - entire sidebar content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            paddingTop: '48px',
            paddingBottom: '24px',
            px: open ? 1 : 0,
            transition: 'padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            // Distinct scrollbar styling for main sidebar scroll (purple/indigo theme)
            "&::-webkit-scrollbar": {
              width: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(0, 0, 0, 0.05)",
              borderRadius: "5px",
              margin: "8px 0",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(99, 102, 241, 0.4)",
              borderRadius: "5px",
              border: "2px solid transparent",
              backgroundClip: "padding-box",
              "&:hover": {
                background: "rgba(99, 102, 241, 0.6)",
                backgroundClip: "padding-box",
              },
            },
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(99, 102, 241, 0.4) rgba(0, 0, 0, 0.05)",
          }}
        >
          {children}
        </Box>
      </Box>
    </DrawerStyled>
  );
};

export default SidebarDrawer;
